import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Batch, Container, SampleDetails} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {ModelManager} from 'models/managers/ModelManager';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import {getStore} from 'redux-stores/Store';
import {restStorableSummaryToGraphQL} from 'samples/storables/graphql/translateStorable';
import {getParentStorableFromAncestralStorables} from 'samples/storables/ParentStorableUtil';
import StorableStore from 'samples/StorableStore';
import APIUtil from 'util/APIUtil';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageRunActions from 'workflows/StageRunActions';

import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

export function makeSampleDetailsFromSamples({
  batch,
  bioentitySummary,
  container,
  plate,
}: {
  batch: Batch | null;
  bioentitySummary: BioentitySummary | null;
  container: Container | null;
  plate: StorableSummaryGraphQL | null;
}): SampleDetails {
  return {
    ...makeEmptySampleDetails(),
    batchInfo: batch
      ? {
          id: batch.id,
          name: batch.name,
          iconId: batch.file.iconId,
          fileId: batch.file.id,
        }
      : null,
    bioentityInfo: bioentitySummary
      ? {
          id: bioentitySummary.id,
          name: bioentitySummary.name,
          displayLabel: bioentitySummary.displayLabel,
        }
      : null,
    containerInfo: container
      ? {
          id: container.id,
          name: container.name,
          barcode: container.barcode,
          ...(container.gridCell && {gridCell: container.gridCell}),
        }
      : null,
    plateInfo: plate
      ? {
          id: plate.id,
          name: plate.name,
          barcode: plate.barcode?.value,
        }
      : null,
  };
}

export default class StageRunSampleModelManager extends ModelManager<ManagedModelType.HYDRATED_STAGE_RUN_SAMPLE_BY_ID> {
  constructor() {
    super(ManagedModelType.HYDRATED_STAGE_RUN_SAMPLE_BY_ID);
  }

  async doFetchMulti(stageRunSampleIds: Array<string>): Promise<void> {
    await StageRunActions.fetchStageRunSamples(stageRunSampleIds, {
      includeBatches: true,
      includeStorableSummaryExtras: true,
    });
  }

  getModel(stageRunSampleId: string): SampleDetails | null {
    const stageRunSample = new WorkflowSelector(getStore().getState().models.workflows).getStageRunSample(
      stageRunSampleId
    );
    if (stageRunSample == null) {
      return null;
    }
    if (stageRunSample.batch == null && stageRunSample.fileId == null) {
      // Stage run sample is inaccessible
      return makeSampleDetailsFromSamples({
        batch: null,
        bioentitySummary: null,
        container: null,
        plate: null,
      });
    }

    let batch: Batch | null = null;
    let bioentitySummary: BioentitySummary | null = null;
    let container: Container | null = null;
    let plate: StorableSummaryGraphQL | null = null;

    if (stageRunSample.batch != null) {
      batch = StorableStore.getBatch(stageRunSample.batch.id);
      if (batch == null) {
        return null;
      }
    } else {
      bioentitySummary = getStore().getState().models.fileSummariesById[stageRunSample.fileId!];
      if (bioentitySummary == null) {
        return null;
      }
    }

    if (stageRunSample.containerId != null) {
      container = StorableStore.getContainer(stageRunSample.containerId);
      if (container == null) {
        return null;
      }

      const {ancestralStorablesById} = StorableStore.getStorableSummaryExtras();
      if (ancestralStorablesById == null || ancestralStorablesById[container.id] == null) {
        return null;
      }
      const location = ancestralStorablesById[container.id]
        ? getParentStorableFromAncestralStorables(
            container,
            ancestralStorablesById[container.id].map((s) => restStorableSummaryToGraphQL(s))
          )
        : null;
      plate =
        location != null && APIUtil.apiIdToType(location.id) === ModelTypeConstants.PLATE ? location : null;
    }

    return makeSampleDetailsFromSamples({batch, bioentitySummary, container, plate});
  }
}
